import { ArrowRight } from "lucide-react";
import { useAssessmentV2 } from "../store";
import { activeScreens, decisionCount } from "../chapters";
import { JourneyRail } from "../components/JourneyRail";
import yomaAvatar from "@/assets/yoma-avatar.png.asset.json";


/**
 * Assessment intro. The one place the brochure's core line lives verbatim.
 * No timers. No urgency. Warm, plain language.
 */
export function IntroScreen() {
  const { state, goto } = useAssessmentV2();
  // The first served screen depends on the band (and on conditionals), so
  // it is read from the walk — never hard-coded to the adult item set.
  const first = activeScreens(state)[0];
  const decisions = decisionCount(state.band);


  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">
        Career Clarity 360
      </div>

      <h1 className="font-display text-4xl md:text-5xl font-medium leading-tight">
        Ready to discover who you're becoming?
      </h1>

      <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
        Your future should be a choice, not a guess.
      </p>

      {/* YoMA introduces herself before anything is asked of the learner.
          Presentation only — she never reads or scores an answer. */}
      <div className="mt-8 flex items-start gap-3 rounded-3xl border border-border bg-card p-5">
        <img
          src={yomaAvatar.url}
          alt="YoMA, your guide"
          data-testid="yoma-avatar"
          className="h-10 w-10 shrink-0 rounded-full object-cover border border-primary/30"
        />

        <p className="text-sm md:text-base text-foreground/90 leading-relaxed">
          <span className="font-medium">Hi, I'm YoMA.</span> I'll walk with you the whole way.
          No scores, no marks — just choices only you can make. Four chapters, and you level up
          through each one.
        </p>
      </div>


      <div className="mt-10 rounded-3xl border border-primary/30 bg-primary/5 p-6 md:p-7">
        <p className="text-base md:text-lg text-foreground/90 leading-relaxed">
          If you're curious about many things, change your mind as you discover new interests,
          love creating, building, gaming or solving problems, are still deciding which path is
          right — or learn differently from the classroom —
        </p>
        <p className="mt-4 font-display text-xl md:text-2xl leading-snug text-foreground">
          those aren't problems. They're signs of curiosity, creativity and untapped potential.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-3 gap-3">
        <Stat value={String(decisions)} label="decisions" />
        <Stat value={state.band === "12to14" ? "~11" : "~13"} label="minutes" />
        <Stat value="5,877" label="careers we check against" />
      </div>

      {/* The whole shape of the walk, up front. Ported from v1, where this
          was the single most reassuring thing on the screen. */}
      <JourneyRail band={state.band} className="mt-8" />


      <div className="mt-10">
        <button
          onClick={() => goto(first)}
          className="min-h-[52px] px-7 rounded-2xl bg-primary text-primary-foreground font-medium
            inline-flex items-center gap-2
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Begin <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="font-display text-2xl md:text-3xl font-medium text-foreground">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground leading-snug">{label}</div>
    </div>
  );
}
